/**
 * The HTTP Status column filter groups exact codes into classes (PRE-3383).
 * Same vocabulary as visibility's `last_status_code_class` and the rest-api
 * BFF: named classes are the hundreds digit (`4xx` → 400–499); `other` is
 * every remaining non-null status (1xx, 0, 6xx+, unofficial codes). A row
 * with no status at all (`lastStatusCode === null`) matches no class.
 */
export const STATUS_CODE_CLASSES = ['2xx', '3xx', '4xx', '5xx', 'other'] as const;

export type StatusCodeClass = (typeof STATUS_CODE_CLASSES)[number];

export const STATUS_CODE_CLASS_LABELS: Record<StatusCodeClass, string> = {
  '2xx': '2xx',
  '3xx': '3xx',
  '4xx': '4xx',
  '5xx': '5xx',
  other: 'Other',
};

const STATUS_CODE_CLASS_SET = new Set<string>(STATUS_CODE_CLASSES);

/** Keep only known classes; empty/unknown becomes `undefined` (no filter). */
export function selectedStatusCodeClasses(
  values: readonly unknown[] | null | undefined
): StatusCodeClass[] | undefined {
  if (!values?.length) return undefined;
  const selected = values.filter((value): value is StatusCodeClass => typeof value === 'string' && STATUS_CODE_CLASS_SET.has(value));
  return selected.length > 0 ? selected : undefined;
}

/** Canonical order so `['5xx','2xx']` and `['2xx','5xx']` share a page scope. */
export function statusFilterScopeKey(classes: StatusCodeClass[] | undefined): string {
  if (!classes?.length) return '';
  return STATUS_CODE_CLASSES.filter((cls) => classes.includes(cls)).join(',');
}

/** Hundreds-digit classification — matches visibility's SQL CASE. */
export function classifyStatusCode(status: number): StatusCodeClass {
  switch (Math.trunc(status / 100)) {
    case 2:
      return '2xx';
    case 3:
      return '3xx';
    case 4:
      return '4xx';
    case 5:
      return '5xx';
    default:
      return 'other';
  }
}
